/**
 * 关联关系申报模块组件（UX 升级版）
 *
 * 实现要求：
 * 1. 网易前员工创办者姓名 + 证件类型/证件号码(Tooltip) + 部门岗位 聚合展示
 * 2. 最早与网易开展合作项目 + 合作年份 聚合展示
 * 3. 是否为网易员工创办者 / 是否与网易开展合作项目 → 标签放入 FormCard leftSlot
 * 4. 两标签均正向（非xxx）时卡片默认折叠；有非正向标签时对应字段只展示标签
 * 5. 删除原有重复的「是否与网易开展合作项目」字段
 */

import { ProText } from '@bedrock/eed-react';
import { Button, Tooltip } from '@bedrock/components';
import { FormCard, FormDescriptions } from '@purchase/supplier';
import { FC, useMemo } from 'react';
import React from 'react';
import { EllipsisTags } from '@mpc/components';

const RelatedPartyDeclaration: FC<{
  formDetail?: any;
}> = ({ formDetail }) => {
  const relatedPartyDeclaration = useMemo(() => {
    return formDetail?.extendAttributeInfo?.relatedPartyDeclaration;
  }, [formDetail]);

  /** 是否为网易前员工创办 */
  const isFoundedByFormer = useMemo(() => {
    return relatedPartyDeclaration?.foundedByFormer;
  }, [relatedPartyDeclaration]);

  /** 是否与网易开展合作项目 */
  const isCooperatingWithNetEase = useMemo(() => {
    return relatedPartyDeclaration?.isCooperatingWithNetEase;
  }, [relatedPartyDeclaration]);

  /** leftSlot 标签列表 */
  const tags = useMemo(() => {
    const list: Array<{ name: string; value: string; color: string }> = [];
    if (isFoundedByFormer) {
      // 非正向（对公司有风险）→ orange
      list.push({
        name: '网易前员工创办',
        value: 'formerEmployee',
        color: 'orange',
      });
    } else {
      // 正向（对公司无风险）→ blue
      list.push({
        name: '非网易前员工创办',
        value: 'nonFormerEmployee',
        color: 'blue',
      });
    }
    if (isCooperatingWithNetEase) {
      // 非正向 → orange
      list.push({
        name: '曾与网易开展合作项目',
        value: 'cooperationProject',
        color: 'orange',
      });
    } else {
      // 正向 → blue
      list.push({
        name: '未曾与网易开展合作项目',
        value: 'nonCooperationProject',
        color: 'blue',
      });
    }
    return list;
  }, [isFoundedByFormer, isCooperatingWithNetEase]);

  /** 两个标签均为正向（blue）时，卡片默认折叠 */
  const allPositive = useMemo(() => {
    return !isFoundedByFormer && !isCooperatingWithNetEase;
  }, [isFoundedByFormer, isCooperatingWithNetEase]);

  /** 曾任职部门及岗位 */
  const departmentAndPosition = useMemo(() => {
    return relatedPartyDeclaration?.formerDepartPosition;
  }, [relatedPartyDeclaration]);

  /** 证件类型 */
  const idType = useMemo(() => {
    return relatedPartyDeclaration?.formerPersonIdType?.name;
  }, [relatedPartyDeclaration]);

  /** 证件号码 */
  const idNumber = useMemo(() => {
    return relatedPartyDeclaration?.formerPersonId;
  }, [relatedPartyDeclaration]);

  /** 合作年份 */
  const cooperationYear = useMemo(() => {
    return relatedPartyDeclaration?.earliestCooperationYear;
  }, [relatedPartyDeclaration]);

  return (
    <FormCard
      title="关联关系申报"
      expandable
      defaultExpanded={!allPositive}
      leftSlot={
        tags.length > 0 && (
          <EllipsisTags
            value={tags}
            tagProps={{ size: 'xSmall' }}
            popoverContentClassName="max-w-[250px]"
          />
        )
      }
    >
      <FormDescriptions columns={2} margin={false}>
        {/* 是否为网易集团前员工创办 */}
        {!allPositive && !isFoundedByFormer && (
          <ProText
            label="是否为网易集团前员工创办"
            name={[
              'extendAttributeInfo',
              'relatedPartyDeclaration',
              'foundedByFormer',
            ]}
            render={(value: boolean | undefined) => {
              return value ? '是' : '否';
            }}
          />
        )}

        {/* 网易前员工创办者信息聚合：姓名 + Tooltip(证件类型/号码) + 部门岗位 */}
        {isFoundedByFormer && (
          <ProText
            label="网易前员工创办者姓名"
            name={[
              'extendAttributeInfo',
              'relatedPartyDeclaration',
              'formerName',
            ]}
            render={(value: string | undefined) => {
              return (
                <div>
                  <div className="flex items-center gap-2">
                    <span>{value || '-'}</span>
                    {(idType || idNumber) && (
                      <Tooltip
                        title={
                          <div>
                            {idType && <div>证件类型：{idType}</div>}
                            {idNumber && <div>证件号码：{idNumber}</div>}
                          </div>
                        }
                      >
                        <Button type="link">查看信息</Button>
                      </Tooltip>
                    )}
                  </div>
                  {Boolean(departmentAndPosition) && (
                    <div className="text-color-3 text-sm mt-1">
                      曾任职部门及岗位：{departmentAndPosition}
                    </div>
                  )}
                </div>
              );
            }}
          />
        )}

        {/* 是否曾与网易开展合作项目 */}
        {!allPositive && !isCooperatingWithNetEase && (
          <ProText
            label="是否曾与网易开展合作项目"
            name={[
              'extendAttributeInfo',
              'relatedPartyDeclaration',
              'isCooperatingWithNetEase',
            ]}
            render={(value: boolean | undefined) => {
              return value ? '是' : '否';
            }}
          />
        )}

        {/* 最早与网易开展合作项目聚合：项目名称 + 合作年份 */}
        {isCooperatingWithNetEase && (
          <ProText
            label="最早与网易开展合作项目"
            name={[
              'extendAttributeInfo',
              'relatedPartyDeclaration',
              'firstCooperationProject',
            ]}
            render={(value: string | undefined) => {
              return (
                <div>
                  <div>{value || '-'}</div>
                  {Boolean(cooperationYear) && (
                    <div className="text-color-3 text-sm mt-1">
                      合作年份：{cooperationYear}
                    </div>
                  )}
                </div>
              );
            }}
          />
        )}
      </FormDescriptions>
    </FormCard>
  );
};

export default RelatedPartyDeclaration;
